/**
 * Selo «Confirmar preço» de um produto ou variante na revisão do import
 * (#299, PR-2; #474 revisões UX). Preço não reconhecido — 0,00/ausente ou
 * «PSM»/«según mercado» impresso no lugar do número — nunca entra no Menu:
 * o commit grava o produto/variante pausado e este selo aponta o que falta
 * ao dono, com o texto impresso VISÍVEL (bdi/dir="auto", Menus RTL) e a
 * explicação (prato inteiro pausado vs só a opção some). Põe o preço no
 * campo da linha (ou da variante) e o selo sai.
 */
import type { ImportDraftProduct } from '../lib/api.js'
import { basePriceFromVariants } from '@self-restaurant/shared'
import { useI18n } from '../lib/i18n.js'

/**
 * Preço EFETIVO do item, com a MESMA regra do commit (#474 UX r3, pontos
 * 2/3): prato com variantes ⇒ base = mínimo das variantes disponíveis
 * (`basePriceFromVariants`, partilhada com o commit); sem variantes ⇒ o
 * próprio `price_cents`. #479: sem NENHUMA variante com preço a regra devolve
 * `null` ⇒ 0 aqui (o selo «Confirmar preço» aparece — o commit pausaria).
 */
export function dishEffectivePriceCents(p: {
  price_cents?: number
  variants?: ReadonlyArray<{ price_cents: number }>
}): number {
  return p.variants !== undefined && p.variants.length > 0
    ? (basePriceFromVariants(p.variants) ?? 0)
    : (p.price_cents ?? 0)
}

/**
 * Preço pede confirmação? (pura — testável sem render). Preço EFETIVO
 * `<= 0` — o mesmo critério do commit, que pausa tudo o que não tem preço
 * reconhecido (nos dois sentidos: [PSM,PSM] preenchido deixa de pedir; base
 * > 0 com variantes todas a 0 passa a pedir).
 */
export function needsPriceConfirm(p: {
  price_status?: 'ok' | 'zero' | 'market'
  price_cents?: number
  variants?: ReadonlyArray<{ price_cents: number }>
}): boolean {
  return dishEffectivePriceCents(p) <= 0
}

/** O que o selo precisa de um produto OU variante do draft (estrutural). */
type PriceLike = Pick<ImportDraftProduct, 'price_cents'> & {
  price_raw?: string
  price_status?: 'ok' | 'zero' | 'market'
  fixed?: boolean
  variants?: ImportDraftProduct['variants']
}

export function ImportReviewPriceBadge({
  product,
  id,
  withHint = true,
  scope = 'dish',
}: {
  product: PriceLike
  /** id estável para aria-describedby do campo de preço (fica único por linha) */
  id?: string
  /** a explicação aparece visível por produto; nas variantes fica no title */
  withHint?: boolean
  /** 'option' = o aviso fala da OPÇÃO (não do prato inteiro) — UX r2, ponto 5 */
  scope?: 'dish' | 'option'
}) {
  const { t } = useI18n()
  if (!needsPriceConfirm(product)) return null
  const badgeId = id ?? 'import-review-price-confirm'
  // QA r3: prato marcado «Corrigir» e AINDA sem preço — o aviso diz que o
  // LLM não alterou o preço e aponta o campo
  const stillMissing = scope === 'dish' && product.fixed === true
  const hintKey = stillMissing
    ? 'admin.import.price_still_missing'
    : scope === 'option'
      ? 'admin.import.price_confirm_hint_option'
      : 'admin.import.price_confirm_hint'
  return (
    <>
      <span
        id={badgeId}
        data-testid="import-review-price-confirm"
        title={t(hintKey)}
        className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 text-xs px-2 py-0.5"
      >
        <span aria-hidden="true">⚠</span>
        <span>{t('admin.import.price_confirm_badge')}</span>
        {product.price_raw && (
          // texto impresso no Menu — pode estar noutra escrita (árabe): bdi/dir=auto
          <bdi dir="auto" className="font-semibold">
            · {product.price_raw}
          </bdi>
        )}
      </span>
      {withHint && (
        <span
          data-testid="import-review-price-hint"
          className="text-[11px] text-amber-700 basis-full"
        >
          {t(hintKey)}
        </span>
      )}
    </>
  )
}
